import difference from 'lodash/difference'

import type { Shortlist } from 'types/shortlist'
import type { EditableTrackMetadata, Track, TrackMetadata } from 'types/track'

type MixedValue = {
  mixed: boolean
  value: string | null | undefined
}

type BasicValueMap = {
  [key: string]: string | null | undefined
}
/**
 * Map of metadata fields to mixed value objects that track both value and mixed state.
 */
export type MixedValueMap = {
  [key: string]: MixedValue
}

function toMixedValue(value: string | null | undefined | number): MixedValue {
  // normalize value, otherwise `undefined` and '' would be treated differenly
  let valueNormalized
  if (value === null) {
    valueNormalized = ''
  } else if (typeof value === 'number') {
    valueNormalized = value + ''
  } else {
    valueNormalized = value || ''
  }
  return {
    mixed: false,
    value: valueNormalized,
  }
}

/**
 * Combines two mixed values, marking as mixed if values differ.
 *
 * @param left - First mixed value
 * @param rightValue - String value to compare with
 * @returns Combined mixed value
 *
 * @example
 * ```typescript
 * addMixedValues({ mixed: false, value: 'Rock' }, 'Pop') // { mixed: true, value: '' }
 * ```
 */
export function addMixedValues(
  left: MixedValue | null | undefined,
  rightValue: string,
): MixedValue {
  const right = toMixedValue(rightValue)
  if (!left) return right
  if (left.mixed) {
    return { mixed: true, value: '' }
  } else if (
    Array.isArray(left.value) &&
    Array.isArray(right.value) &&
    left.value.length === right.value.length &&
    difference(left.value, right.value).length === 0
  ) {
    return left
  } else if (left.value !== right.value) {
    return { mixed: true, value: '' }
  } else {
    // when left equals right
    return left
  }
}

const getMixedMeta = (
  items: Array<TrackMetadata> | Array<Partial<Track>>,
  fields: string[],
  fieldMapping?: {
    [key: string]: string
  } | null,
) => {
  return fields.reduce<Record<string, any>>(
    (meta, fieldName) => ({
      ...meta,
      [(fieldMapping && fieldMapping[fieldName]) || fieldName]:
        items.reduce<MixedValue>(
          (acc, item) => addMixedValues(acc, item[fieldName]),
          null as any,
        ),
    }),
    {},
  )
}

/**
 * Extracts common basic metadata fields from tracks.
 *
 * @param items - Array of tracks
 * @returns Map of common metadata values
 *
 * @example
 * ```typescript
 * getCommonBasicMetadataFields(tracks)
 * ```
 */
export function getCommonBasicMetadataFields(
  items: Array<Partial<Track>>,
): MixedValueMap {
  return getMixedMeta(items, ['artwork', 'name', 'album', 'artist'], {
    artwork: 'Artwork',
    name: 'Title',
    album: 'Album',
    artist: 'Artist',
  })
}

/** Standard metadata fields displayed in the main metadata editor. */
export const commonMetadataFields = ['Artwork', 'Title', 'Album', 'Artist']
/**
 * Additional metadata fields in the expanded editor. Copyright is intentionally
 * omitted (flag-gated, preserved) so an empty value is never seeded into saves;
 * it surfaces via a track's own metadata instead.
 */
export const extraMetadataFields = [
  'Composer',
  'Grouping',
  'Genre',
  'Year',
  'Lyrics',
  'Comment',
  'TrackNumber',
  'Tracks',
  'BPM',
  'ProNumber',
  'ISRC',
  'External Id',
] as const
// This function is very similar to `getCommonBasicMetadataFields`
// It gets same attributes, but slightly different:
// - upper case
// - with different names (name => Title)
//
// We don't want to merge Album/Artist here as user could already edit them
/**
 * Extracts common extra metadata fields from track metadata.
 *
 * @param items - Array of track metadata
 * @returns Map of common extra metadata values
 *
 * @example
 * ```typescript
 * getCommonExtraMetadataFields(tracks.map(t => t.metadata))
 * ```
 */
export function getCommonExtraMetadataFields(
  items: Array<TrackMetadata>,
): MixedValueMap {
  const keys = extraMetadataFields.slice()
  items.forEach(metadata =>
    Object.keys(metadata).forEach(
      (metaKey: (typeof extraMetadataFields)[number]) => keys.push(metaKey),
    ),
  )
  const extraKeys = [...new Set(keys)].filter(
    key => !commonMetadataFields.includes(key),
  )

  return getMixedMeta(items, extraKeys)
}

function toInt(number?: string | null) {
  return number === null || number === undefined ? null : parseInt(number, 10)
}

/**
 * Converts mixed metadata format to basic key-value format.
 *
 * @param mixedMetadata - Metadata with mixed value indicators
 * @returns Plain metadata without mixed value indicators
 *
 * @example
 * ```typescript
 * convertMixedMetadataToBasicMetadata(mixedMetadata)
 * ```
 */
export function convertMixedMetadataToBasicMetadata(
  mixedMetadata: EditableTrackMetadata,
): BasicValueMap {
  const basicMetadata: Record<string, any> = {}
  Object.keys(mixedMetadata).forEach(key => {
    const field = mixedMetadata[key]
    if (field && !field.mixed) {
      basicMetadata[key] = field.value
    }
  })

  basicMetadata.Tracks = toInt(basicMetadata.Tracks)
  basicMetadata.TrackNumber = toInt(basicMetadata.TrackNumber)

  return basicMetadata
}

/**
 * Gets initial metadata values for the metadata editor.
 *
 * @param shortlist - Shortlist containing tracks
 * @returns Map of initial metadata values
 *
 * @example
 * ```typescript
 * getInitialMetadataValues(shortlist)
 * ```
 */
export const getInitialMetadataValues = (
  shortlist: Shortlist,
): MixedValueMap => {
  const { items, playlist_aliases } = shortlist
  const tracks = items.map(item => item.track)

  const aliasValues: Record<string, any> = {}
  if (playlist_aliases) {
    Object.keys(playlist_aliases).forEach(key => {
      if (playlist_aliases[key]?.value !== '') {
        aliasValues[key] = {
          mixed: false,
          value: playlist_aliases[key]?.value,
        }
      }
    })
  }

  return {
    ...getCommonBasicMetadataFields(tracks),
    ...getCommonExtraMetadataFields(tracks.map(t => t.metadata)),
    ...aliasValues,
  }
}
